import { useCallback, useEffect, useRef, useState } from 'react'

// 显示区最大边长（内部坐标按图片自然像素计算，显示时按比例缩放）
const VIEW_MAX = 480
const HANDLE_R = 12
const MIN_CROP = 8

const ASPECTS = [
  { id: 'free', name: '自由裁剪', ratio: null },
  { id: '1:1', name: '1:1 方形', ratio: 1 },
  { id: '16:9', name: '16:9 横幅', ratio: 16 / 9 },
  { id: 'original', name: '不裁剪（原图）', ratio: null },
]

// 按比例计算居中默认裁剪框（覆盖图片 90% 区域）
function defaultCrop(imgW, imgH, ratio) {
  if (!ratio) {
    const m = 0.05
    return { x: imgW * m, y: imgH * m, w: imgW * (1 - m * 2), h: imgH * (1 - m * 2) }
  }
  let w = imgW * 0.9
  let h = w / ratio
  if (h > imgH * 0.9) {
    h = imgH * 0.9
    w = h * ratio
  }
  return { x: (imgW - w) / 2, y: (imgH - h) / 2, w, h }
}

function clampCrop(r, imgW, imgH) {
  const w = Math.min(Math.max(r.w, MIN_CROP), imgW)
  const h = Math.min(Math.max(r.h, MIN_CROP), imgH)
  return {
    x: Math.min(Math.max(r.x, 0), imgW - w),
    y: Math.min(Math.max(r.y, 0), imgH - h),
    w,
    h,
  }
}

export default function ImageCropModal({ open, src, onCancel, onConfirm }) {
  const canvasRef = useRef(null)
  const [img, setImg] = useState(null)
  const [aspect, setAspect] = useState('1:1')
  const [crop, setCrop] = useState(null)
  const dragRef = useRef(null) // { mode: 'move'|'nw'|'ne'|'sw'|'se', startX, startY, startCrop }

  const ratio = ASPECTS.find((a) => a.id === aspect)?.ratio ?? null

  // 使用 ref 稳定 onCancel 引用，避免父组件重渲染导致图片重新加载
  const onCancelRef = useRef(onCancel)
  onCancelRef.current = onCancel

  useEffect(() => {
    if (!open || !src) return
    setAspect('1:1')
    setImg(null)
    setCrop(null)
    // base64 / blob URL 不需要 crossOrigin，避免某些浏览器误处理导致 canvas 污染
    const isLocal = src.startsWith('data:') || src.startsWith('blob:')
    const image = new Image()
    if (!isLocal) {
      image.crossOrigin = 'anonymous'
    }
    image.onload = () => {
      setImg(image)
      setCrop(defaultCrop(image.width, image.height, 1))
    }
    image.onerror = () => {
      console.error('[ImageCropModal] failed to load image:', src.slice(0, 100))
      // 尝试不跨域重载
      const img2 = new Image()
      img2.onload = () => {
        setImg(img2)
        setCrop(defaultCrop(img2.width, img2.height, 1))
      }
      img2.onerror = () => {
        console.error('[ImageCropModal] fallback load also failed')
        alert('图片加载失败，请尝试重新上传')
        onCancelRef.current()
      }
      img2.src = src
    }
    image.src = src
  }, [open, src])

  const scale = img ? Math.min(VIEW_MAX / img.width, VIEW_MAX / img.height, 1) : 1

  // 切换比例时重置裁剪框
  const changeAspect = (id) => {
    setAspect(id)
    if (!img) return
    const r = ASPECTS.find((a) => a.id === id)?.ratio ?? null
    setCrop(defaultCrop(img.width, img.height, r))
  }

  const draw = useCallback(() => {
    if (!img || !canvasRef.current) return
    const canvas = canvasRef.current
    canvas.width = Math.round(img.width * scale)
    canvas.height = Math.round(img.height * scale)
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

    if (aspect === 'original' || !crop) return

    const cx = crop.x * scale
    const cy = crop.y * scale
    const cw = crop.w * scale
    const ch = crop.h * scale

    // 遮罩（裁剪框外压暗）
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
    ctx.fillRect(0, 0, canvas.width, cy)
    ctx.fillRect(0, cy + ch, canvas.width, canvas.height - cy - ch)
    ctx.fillRect(0, cy, cx, ch)
    ctx.fillRect(cx + cw, cy, canvas.width - cx - cw, ch)

    // 裁剪框
    ctx.strokeStyle = '#ff4d6d'
    ctx.lineWidth = 2
    ctx.strokeRect(cx, cy, cw, ch)

    // 三分线
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)'
    ctx.lineWidth = 1
    for (const t of [1 / 3, 2 / 3]) {
      ctx.beginPath()
      ctx.moveTo(cx + cw * t, cy)
      ctx.lineTo(cx + cw * t, cy + ch)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(cx, cy + ch * t)
      ctx.lineTo(cx + cw, cy + ch * t)
      ctx.stroke()
    }

    // 四角手柄
    ctx.fillStyle = '#ff4d6d'
    for (const [hx, hy] of [[cx, cy], [cx + cw, cy], [cx, cy + ch], [cx + cw, cy + ch]]) {
      ctx.beginPath()
      ctx.arc(hx, hy, 6, 0, Math.PI * 2)
      ctx.fill()
    }
  }, [img, crop, scale, aspect])

  useEffect(() => {
    draw()
  }, [draw])

  const toImagePoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect()
    return {
      x: (e.clientX - rect.left) * (canvasRef.current.width / rect.width) / scale,
      y: (e.clientY - rect.top) * (canvasRef.current.height / rect.height) / scale,
    }
  }

  const hitHandle = (p) => {
    if (!crop) return null
    const r = HANDLE_R / scale
    const corners = {
      nw: [crop.x, crop.y],
      ne: [crop.x + crop.w, crop.y],
      sw: [crop.x, crop.y + crop.h],
      se: [crop.x + crop.w, crop.y + crop.h],
    }
    for (const [key, [hx, hy]] of Object.entries(corners)) {
      if (Math.hypot(p.x - hx, p.y - hy) < r) return key
    }
    return null
  }

  const handlePointerDown = (e) => {
    if (!img || !crop || aspect === 'original') return
    const p = toImagePoint(e)
    const handle = hitHandle(p)
    const inside =
      p.x >= crop.x && p.x <= crop.x + crop.w && p.y >= crop.y && p.y <= crop.y + crop.h
    if (!handle && !inside) return
    dragRef.current = {
      mode: handle || 'move',
      startX: p.x,
      startY: p.y,
      startCrop: { ...crop },
    }
    canvasRef.current.setPointerCapture?.(e.pointerId)
  }

  const handlePointerMove = (e) => {
    const drag = dragRef.current
    if (!drag || !img) return
    const p = toImagePoint(e)
    const dx = p.x - drag.startX
    const dy = p.y - drag.startY
    const s = drag.startCrop

    if (drag.mode === 'move') {
      setCrop(clampCrop({ ...s, x: s.x + dx, y: s.y + dy }, img.width, img.height))
      return
    }

    // 角手柄拖拽改大小
    let { x, y, w, h } = s
    if (drag.mode.includes('e')) w = s.w + dx
    if (drag.mode.includes('s')) h = s.h + dy
    if (drag.mode.includes('w')) {
      x = s.x + dx
      w = s.w - dx
    }
    if (drag.mode.includes('n')) {
      y = s.y + dy
      h = s.h - dy
    }
    if (ratio) {
      // 锁定比例：以拖动方向的较大变化为准，锚定对角
      if (Math.abs(w / s.w) > Math.abs(h / s.h)) h = w / ratio
      else w = h * ratio
      if (drag.mode.includes('w')) x = s.x + s.w - w
      if (drag.mode.includes('n')) y = s.y + s.h - h
    }
    setCrop(clampCrop({ x, y, w, h }, img.width, img.height))
  }

  const handlePointerUp = () => {
    dragRef.current = null
  }

  const handleConfirm = () => {
    if (!img) return
    if (aspect === 'original' || !crop) {
      onConfirm(src)
      return
    }
    const w = Math.max(1, Math.round(crop.w))
    const h = Math.max(1, Math.round(crop.h))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    try {
      ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, w, h)
      const dataUrl = canvas.toDataURL('image/png')
      onConfirm(dataUrl)
    } catch (err) {
      console.error('[ImageCropModal] canvas export failed:', err)
      // 如果 canvas 被污染，直接返回原图（不做裁剪）
      onConfirm(src)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex w-full max-w-2xl flex-col gap-4 rounded-3xl bg-white p-5 shadow-card max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-cocoa">✂️ 裁剪图片</h3>
          <button
            onClick={onCancel}
            className="rounded-full p-1 text-cocoa-light transition hover:bg-cream-100 hover:text-cocoa"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {ASPECTS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => changeAspect(a.id)}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                aspect === a.id
                  ? 'border-blush bg-blush text-white'
                  : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
              }`}
            >
              {a.name}
            </button>
          ))}
        </div>

        <div className="flex justify-center overflow-hidden rounded-2xl border border-lavender/30 bg-cream-100">
          <canvas
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            className="max-w-full touch-none select-none"
            style={{ cursor: aspect === 'original' ? 'default' : 'move' }}
          />
        </div>

        <p className="text-xs text-cocoa-light">
          {aspect === 'original'
            ? '使用原图，不裁剪。'
            : '拖动裁剪框移动位置，拖动四角调整大小。'}
          {img && crop && aspect !== 'original' && (
            <span className="ml-2">
              裁剪区域：{Math.round(crop.w)} × {Math.round(crop.h)} px
            </span>
          )}
        </p>

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-full border border-lavender/60 px-5 py-2 text-sm font-medium text-cocoa transition hover:bg-lavender/30"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            className="rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-blush-dark hover:shadow-md"
          >
            确定
          </button>
        </div>
      </div>
    </div>
  )
}
